import { useState, useEffect, useCallback } from 'react';
import { ScrollText } from 'lucide-react';
import { auditApi, AuditLogRow } from '../../api/learner';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Sheet, SheetBody, SheetContent, SheetHeader } from '../../components/ui/sheet';
import { Pagination } from '../../components/shared/Pagination';
import {
  EmptyState,
  ErrorState,
  FilterChips,
  FormField,
  PageHeader,
  StatusPill,
  Toolbar,
  type FilterChip,
  type StatusTone,
} from '@/components/app';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { tashkentDayEndIso, tashkentDayStartIso, TASHKENT_TZ } from '@/lib/datetime';

const PAGE_SIZE = 50;

const TARGET_LABEL: Record<string, string> = {
  user: 'Пользователь',
  order: 'Заказ',
  payment: 'Платёж',
  plan: 'Тариф',
  promo_code: 'Промокод',
  notification: 'Уведомление',
  video_lesson: 'Видеоурок',
  setting: 'Настройка',
  expense: 'Расход',
  payer: 'Плательщик',
};

const fmtNum = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

const formatDate = (s: string) =>
  new Date(s).toLocaleString('ru-RU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: TASHKENT_TZ,
  });

const formatDay = (d: string) => d.split('-').reverse().join('.');

/** HTTP-код ответа → тон статуса. */
function statusTone(code: number): StatusTone {
  if (code >= 200 && code < 300) return 'pos';
  if (code >= 400 && code < 500) return 'warn';
  if (code >= 500) return 'neg';
  return 'neutral';
}

// Пропсы маршрута (App.tsx) не используются: тема приходит из CSS-переменных.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function AuditLogPage(_props: Record<string, unknown>) {
  const [items, setItems] = useState<AuditLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adminIdFilter, setAdminIdFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<AuditLogRow | null>(null);
  const [adminIdInput, setAdminIdInput] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  // Текстовые фильтры применяются через 300 мс после ввода; страница — на первую.
  const applyAdminId = useDebouncedCallback((v: string) => { setAdminIdFilter(v); setPage(1); });
  const applyAction = useDebouncedCallback((v: string) => { setActionFilter(v); setPage(1); });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: Parameters<typeof auditApi.list>[0] = { page, pageSize: PAGE_SIZE };
      if (adminIdFilter.trim()) params.adminId = adminIdFilter.trim();
      if (actionFilter.trim()) params.action = actionFilter.trim();
      if (targetTypeFilter !== 'all') params.targetType = targetTypeFilter;
      // Границы дня — по Ташкенту, «по» включает выбранный день целиком.
      if (from) params.from = tashkentDayStartIso(from) ?? undefined;
      if (to) params.to = tashkentDayEndIso(to) ?? undefined;
      const res = await auditApi.list(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Не удалось загрузить аудит-лог'));
    } finally {
      setLoading(false);
    }
  }, [page, adminIdFilter, actionFilter, targetTypeFilter, from, to]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearAdminId = () => { setAdminIdInput(''); setAdminIdFilter(''); setPage(1); };
  const clearAction = () => { setActionInput(''); setActionFilter(''); setPage(1); };

  const chips: FilterChip[] = [];
  if (adminIdFilter.trim()) chips.push({ key: 'admin', label: `Админ: ${adminIdFilter.trim()}`, onRemove: clearAdminId });
  if (actionFilter.trim()) chips.push({ key: 'action', label: `Действие: ${actionFilter.trim()}`, onRemove: clearAction });
  if (targetTypeFilter !== 'all') {
    chips.push({
      key: 'target',
      label: `Объект: ${TARGET_LABEL[targetTypeFilter] ?? targetTypeFilter}`,
      onRemove: () => { setTargetTypeFilter('all'); setPage(1); },
    });
  }
  if (from || to) {
    chips.push({
      key: 'dates',
      label: `Период: ${from ? formatDay(from) : '…'} – ${to ? formatDay(to) : '…'}`,
      onRemove: () => { setFrom(''); setTo(''); setPage(1); },
    });
  }
  const resetAll = () => {
    clearAdminId();
    clearAction();
    setTargetTypeFilter('all');
    setFrom('');
    setTo('');
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Аудит-лог админов"
        subtitle="Все изменяющие действия админов записываются автоматически"
      />

      <Card className="p-[18px]">
        <section aria-label="Фильтры" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <FormField label="ID админа" tone="filter">
            <Input
              placeholder="UUID или его начало"
              value={adminIdInput}
              onChange={(e) => { setAdminIdInput(e.target.value); applyAdminId(e.target.value); }}
            />
          </FormField>
          <FormField label="Действие" tone="filter">
            <Input
              placeholder="Содержит…"
              value={actionInput}
              onChange={(e) => { setActionInput(e.target.value); applyAction(e.target.value); }}
            />
          </FormField>
          <FormField label="Тип объекта" tone="filter">
            {(p) => (
              <Select value={targetTypeFilter} onValueChange={(v) => { setTargetTypeFilter(v); setPage(1); }}>
                <SelectTrigger {...p}>
                  <SelectValue placeholder="Все объекты" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все объекты</SelectItem>
                  {Object.entries(TARGET_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label="С даты" tone="filter">
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </FormField>
          <FormField label="По дату (включительно)" tone="filter">
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </FormField>
        </section>
      </Card>

      <Card className="overflow-hidden">
        <Toolbar title={!loading && !loadError ? `Записей ${fmtNum(total)}` : 'Записи'}>
          <FilterChips chips={chips} onReset={resetAll} />
        </Toolbar>

        {loadError ? (
          <ErrorState message={loadError} onRetry={fetchItems} />
        ) : !loading && items.length === 0 ? (
          <EmptyState icon={<ScrollText />} title="Записей нет" description="По этим фильтрам ничего не найдено." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Когда</TableHead>
                <TableHead>Админ</TableHead>
                <TableHead>Действие</TableHead>
                <TableHead>Объект</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }, (_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }, (__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.map((row) => (
                    <TableRow
                      key={row.id}
                      selected={selected?.id === row.id}
                      className="cursor-pointer"
                      tabIndex={0}
                      onClick={() => setSelected(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelected(row);
                        }
                      }}
                    >
                      <TableCell className="whitespace-nowrap text-xs tabular-nums text-text-2">{formatDate(row.created_at)}</TableCell>
                      <TableCell className="text-xs">
                        {row.admin_username ?? <span className="font-mono">{row.admin_id?.slice(0, 8)}…</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.action}</TableCell>
                      <TableCell className="text-xs">
                        {row.target_type ? (
                          <span>
                            <span className="font-semibold">{TARGET_LABEL[row.target_type] ?? row.target_type}</span>
                            {row.target_id && (
                              <span className="ml-1 font-mono text-text-2">{row.target_id.slice(0, 8)}…</span>
                            )}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <StatusPill size="sm" tone={statusTone(row.status_code)} className="tabular-nums">
                          {row.status_code}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-text-2">{row.ip_address ?? '—'}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}

        {!loadError && totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            total={total}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent aria-describedby={undefined}>
          <SheetHeader title="Запись аудит-лога" />
          {selected && (
            <SheetBody>
              <Card className="p-[18px]">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
                  <dt className="text-text-2">Когда</dt>
                  <dd className="m-0 tabular-nums">{formatDate(selected.created_at)}</dd>
                  <dt className="text-text-2">Админ</dt>
                  <dd className="m-0 break-all">
                    {selected.admin_username ?? '—'}
                    {selected.admin_id && <span className="ml-1 font-mono text-xs text-text-2">{selected.admin_id}</span>}
                  </dd>
                  <dt className="text-text-2">Действие</dt>
                  <dd className="m-0 break-all font-mono text-xs">{selected.action}</dd>
                  <dt className="text-text-2">Объект</dt>
                  <dd className="m-0 break-all">
                    {selected.target_type ? (TARGET_LABEL[selected.target_type] ?? selected.target_type) : '—'}
                    {selected.target_id && <span className="ml-1 font-mono text-xs text-text-2">{selected.target_id}</span>}
                  </dd>
                  <dt className="text-text-2">Статус</dt>
                  <dd className="m-0">
                    <StatusPill size="sm" tone={statusTone(selected.status_code)} className="tabular-nums">
                      {selected.status_code}
                    </StatusPill>
                  </dd>
                  <dt className="text-text-2">IP</dt>
                  <dd className="m-0 font-mono text-xs">{selected.ip_address ?? '—'}</dd>
                  <dt className="text-text-2">User-Agent</dt>
                  <dd className="m-0 break-all text-xs text-text-2">{selected.user_agent ?? '—'}</dd>
                </dl>
              </Card>
              <Card className="p-[18px]">
                <b className="font-display text-[15px] font-bold">Данные запроса</b>
                {selected.payload ? (
                  <pre className="mt-3 max-h-[420px] overflow-auto rounded-xl bg-surface-alt p-3 text-xs text-text">
                    {JSON.stringify(selected.payload, null, 2)}
                  </pre>
                ) : (
                  <p className="mt-2 text-sm text-text-2">Тело запроса не записано.</p>
                )}
              </Card>
            </SheetBody>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
